import React from 'react';

/**
 * Panel de totales de cotización con editor de descuento.
 *
 * @param {Object} props
 * @param {Object} props.cotizacion - Datos de la cotización (descuento_porcentaje, descuento_monto)
 * @param {boolean} props.isEditable - Si la cotización es editable (estado borrador)
 * @param {number} props.subtotal - Subtotal calculado
 * @param {number} props.total - Total calculado
 * @param {number} props.descuentoAplicado - Monto de descuento aplicado
 * @param {boolean} props.showDescuentoForm - Si muestra el formulario de descuento
 * @param {string} props.descuentoTipo - Tipo de descuento (porcentaje|monto)
 * @param {string} props.descuentoValor - Valor del descuento
 * @param {boolean} props.savingDescuento - Si está guardando descuento
 * @param {Function} props.onOpenDescuento - Abrir editor de descuento
 * @param {Function} props.onSaveDescuento - Guardar descuento
 * @param {Function} props.onQuitarDescuento - Quitar descuento
 * @param {Function} props.onSetDescuentoTipo - Cambiar tipo de descuento
 * @param {Function} props.onSetDescuentoValor - Cambiar valor de descuento
 * @param {Function} props.onCancelDescuento - Cancelar edición de descuento
 * @returns {JSX.Element}
 */
function CotizacionTotales({
  cotizacion, isEditable,
  subtotal, total, descuentoAplicado,
  showDescuentoForm, descuentoTipo, descuentoValor, savingDescuento,
  onOpenDescuento, onSaveDescuento, onQuitarDescuento,
  onSetDescuentoTipo, onSetDescuentoValor, onCancelDescuento
}) {
  return (
    <div className="card cotizacion-totales">
      <div className="totales-row">
        <span className="totales-label">Subtotal</span>
        <span className="totales-value">${subtotal.toLocaleString('es-CL')}</span>
      </div>

      {(cotizacion.descuento_porcentaje > 0 || cotizacion.descuento_monto > 0) && (
        <div className="totales-row totales-row--discount">
          <span className="totales-label">
            Descuento
            {cotizacion.descuento_porcentaje > 0 && <span className="text-muted text-sm"> ({cotizacion.descuento_porcentaje}%)</span>}
          </span>
          <span className="totales-value text-danger">-${descuentoAplicado.toLocaleString('es-CL')}</span>
        </div>
      )}

      {isEditable && (
        <div className="descuento-editor">
          {showDescuentoForm ? (
            <div className="descuento-form">
              <div className="descuento-tipo">
                <label className="descuento-tipo-option">
                  <input type="radio" name="descuentoTipo" value="porcentaje" checked={descuentoTipo === 'porcentaje'} onChange={() => onSetDescuentoTipo('porcentaje')} /> %
                </label>
                <label className="descuento-tipo-option">
                  <input type="radio" name="descuentoTipo" value="monto" checked={descuentoTipo === 'monto'} onChange={() => onSetDescuentoTipo('monto')} /> $ fijo
                </label>
              </div>
              <div className="display-flex gap-sm align-center">
                <input
                  type="number" className="form-control"
                  placeholder={descuentoTipo === 'porcentaje' ? '0 – 100' : 'Monto'}
                  value={descuentoValor} min={0}
                  max={descuentoTipo === 'porcentaje' ? 100 : undefined}
                  step={descuentoTipo === 'porcentaje' ? 0.01 : 1}
                  onChange={(e) => onSetDescuentoValor(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSaveDescuento(); }}
                />
                <button className="btn btn-sm btn-primary" onClick={onSaveDescuento} disabled={savingDescuento || descuentoValor === ''}>
                  {savingDescuento ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={onCancelDescuento} disabled={savingDescuento}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          ) : (
            <div className="display-flex gap-sm">
              <button className="btn btn-sm btn-ghost-primary" onClick={onOpenDescuento}>
                <i className="fas fa-tag"></i> {cotizacion.descuento_porcentaje > 0 || cotizacion.descuento_monto > 0 ? 'Editar descuento' : 'Agregar descuento'}
              </button>
              {(cotizacion.descuento_porcentaje > 0 || cotizacion.descuento_monto > 0) && (
                <button className="btn btn-sm btn-ghost-danger" onClick={onQuitarDescuento} disabled={savingDescuento} title="Quitar descuento">
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="totales-row totales-row--total">
        <span className="totales-label font-bold">TOTAL</span>
        <span className="totales-value totales-value--total">${total.toLocaleString('es-CL')}</span>
      </div>
    </div>
  );
}

export default CotizacionTotales;
